import { __decorate } from "tslib";
import { LitElement, html } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { scrollBy } from 'seamless-scroll-polyfill';
import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import chevronLeftIcon from '../../assets/icons/chevron-left';
import chevronRightIcon from '../../assets/icons/chevron-right';
import verticalDotsIcon from '../../assets/icons/vertical-dots';
import '../icon';
import '../chip';
import '../button';
import styles from './styles';
/**
 * @dependency amityeko-icon
 * @dependency amityeko-chip
 * @dependency amityeko-button
 *
 * @event amityeko-select {{ id }} - Emmited when a chip is selected.
 * @event amityeko-select-chips-selection-dropdown {{ chipId, dropdownOptionValue }} - Emitted
 *        when a dropdown item of a chip is selected.
 */
export default class AmityekoChipsSelection extends LitElement {
    constructor() {
        super(...arguments);
        this.options = [];
        this.dropdownOptions = [];
        // This is length of scroll when left of right button click.
        this.scrollLength = 0;
        this.isScrollDisabled = true;
        this.isClickEnabled = true;
        this.isLeftBtnHidden = true;
        this.isRightBtnHidden = false;
        this.firstChipPosition = 0;
        this.lastChipPosition = 0;
    }
    updated(changedProperties) {
        //Check whether options property is updated
        if (!changedProperties.has('options'))
            return;
        const oldValue = changedProperties.get('options');
        const newValue = this.options;
        if (newValue.length && JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            setTimeout(() => {
                var _a, _b, _c, _d, _e, _f, _g, _h;
                /**
                 * Set the value of the "scrollLength" to the width of the "chipListEl" element
                 * Note: clientWidth is the width of the chipListEl (only visual part not including hidden part)
                 */
                this.scrollLength = (_a = this.chipListEl) === null || _a === void 0 ? void 0 : _a.clientWidth;
                /**
                 * set initial position for the first chip of chipList element
                 * Note: offsetLeft is the left position of element to its parent element(this.chipListEl) not with viewport
                 */
                //@ts-ignore
                this.firstChipPosition = ((_c = (_b = this.chipListEl) === null || _b === void 0 ? void 0 : _b.firstElementChild) === null || _c === void 0 ? void 0 : _c.offsetLeft) || 0;
                /**
                 * set initial position for the last chip of chipList element
                 * offsetLeft + offsetWidth (to get right position of last chip)
                 */
                this.lastChipPosition =
                    //@ts-ignore
                    ((_e = (_d = this.chipListEl) === null || _d === void 0 ? void 0 : _d.lastElementChild) === null || _e === void 0 ? void 0 : _e.offsetLeft) +
                        (
                        //@ts-ignore
                        (_g = (_f = this.chipListEl) === null || _f === void 0 ? void 0 : _f.lastElementChild) === null || _g === void 0 ? void 0 : _g.offsetWidth) || 0;
                this.isScrollDisabled = ((_h = this.chipListEl) === null || _h === void 0 ? void 0 : _h.scrollWidth) === this.scrollLength;
                if (!this.isScrollDisabled) {
                    this._updateRightButtonHiddenFlag();
                }
            });
        }
    }
    _setLeftScrollPosition(left) {
        scrollBy(this.chipListEl, {
            behavior: 'smooth',
            top: 0,
            left,
        });
    }
    _updateLeftButtonHiddenFlag() {
        //If firstChipPosition is less than scrollLength, hide the left scroll button
        if (this.firstChipPosition < this.scrollLength) {
            this.isLeftBtnHidden = true;
        }
    }
    _updateRightButtonHiddenFlag() {
        //If lastChipPosition is less than or equal to scrollLength, hide the right scroll button
        if (this.lastChipPosition <= this.scrollLength) {
            this.isRightBtnHidden = true;
        }
    }
    _onClickChip(optionId) {
        emit(this, 'amityeko-select', { detail: { id: optionId } });
    }
    _onClickLeftScroll(e) {
        e.stopPropagation();
        //scroll to right
        this._setLeftScrollPosition(-this.scrollLength);
        /**
         * If user click left button, subtract scrollLength from "firstChipPosition"
         * eg: current firstChipPosition is 1500 and scrollLength is 500 (1500 - 500 etc)
         */
        this.firstChipPosition = this.firstChipPosition - this.scrollLength;
        /**
         * If user click left button, add scrollLength to "lastChipPosition"
         * eg: current lastChipPosition is 300 and scrollLength is 500 (300 + 500 etc)
         */
        this.lastChipPosition = this.lastChipPosition + this.scrollLength;
        this.isRightBtnHidden = false;
        this._updateLeftButtonHiddenFlag();
    }
    _onClickRightScroll(e) {
        e.stopPropagation();
        //scroll to left
        this._setLeftScrollPosition(this.scrollLength);
        /**
         * If user click right button, add scrollLength to "firstChipPosition"
         * eg: initial firstChipPosition is 0 and scrollLength is 500 (0 + 500 etc)
         */
        this.firstChipPosition = this.firstChipPosition + this.scrollLength;
        /**
         * If user click right button, subtract scrollLength from "lastChipPosition"
         * eg: initial lastChipPosition is 1800 and scrollLength is 500 (1800 - 500 etc)
         */
        this.lastChipPosition = this.lastChipPosition - this.scrollLength;
        this.isLeftBtnHidden = false;
        this._updateRightButtonHiddenFlag();
    }
    _onSelectChipDropdown(e, id) {
        e.stopPropagation();
        const detail = { chipId: id, dropdownOptionValue: e.detail.value };
        emit(this, 'amityeko-select-chips-selection-dropdown', { detail });
    }
    render() {
        return html `
      <div
        class="chips ${classMap({
            'chips--no-scroll': this.isScrollDisabled,
        })}"
      >
        <amityeko-button
          circle
          .variant=${'ghost'}
          @click=${this._onClickLeftScroll}
          class="chips__scroll-btn chips__scroll-btn--left ${classMap({
            'chips__scroll-btn--hidden': this.isLeftBtnHidden,
        })}"
        >
          <amityeko-icon .iconSize=${'12px'}>${chevronLeftIcon}</amityeko-icon>
        </amityeko-button>
        <div id="chips_list" class="chips__list">
          ${repeat(this.options, option => option.id, option => html `
                <amityeko-chip
                  clickable
                  id=${option.id}
                  .selectionColor=${option.selectionColor}
                  .dropdownOptions=${this.dropdownOptions}
                  ?selected=${this.selectedId === option.id}
                  @amityeko-select-chip-dropdown=${(e) => {
            this._onSelectChipDropdown(e, option.id);
        }}
                  @amityeko-click-chip=${() => this._onClickChip(option.id)}
                >
                  ${option.label}
                  <amityeko-icon slot="button-icon">${verticalDotsIcon}</amityeko-icon>
                </amityeko-chip>
              `)}
        </div>
        <amityeko-button
          circle
          .variant=${'ghost'}
          @click=${this._onClickRightScroll}
          class="chips__scroll-btn chips__scroll-btn--right ${classMap({
            'chips__scroll-btn--hidden': this.isRightBtnHidden,
        })}"
        >
          <amityeko-icon .iconSize=${'12px'}>${chevronRightIcon}</amityeko-icon>
        </amityeko-button>
      </div>
    `;
    }
}
AmityekoChipsSelection.styles = styles;
__decorate([
    query('#chips_list')
], AmityekoChipsSelection.prototype, "chipListEl", void 0);
__decorate([
    property({ type: Array })
], AmityekoChipsSelection.prototype, "options", void 0);
__decorate([
    property({ type: Array })
], AmityekoChipsSelection.prototype, "dropdownOptions", void 0);
__decorate([
    state()
], AmityekoChipsSelection.prototype, "scrollLength", void 0);
__decorate([
    property()
], AmityekoChipsSelection.prototype, "selectedId", void 0);
__decorate([
    state()
], AmityekoChipsSelection.prototype, "isScrollDisabled", void 0);
__decorate([
    state()
], AmityekoChipsSelection.prototype, "isClickEnabled", void 0);
__decorate([
    state()
], AmityekoChipsSelection.prototype, "isLeftBtnHidden", void 0);
__decorate([
    state()
], AmityekoChipsSelection.prototype, "isRightBtnHidden", void 0);
__decorate([
    state()
], AmityekoChipsSelection.prototype, "firstChipPosition", void 0);
__decorate([
    state()
], AmityekoChipsSelection.prototype, "lastChipPosition", void 0);
if (!customElements.get('amityeko-chips-selection')) {
    customElements.define('amityeko-chips-selection', AmityekoChipsSelection);
}
//# sourceMappingURL=index.js.map